
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material"
import { useState } from "react"
import useDebounce from "../../../../../../../../lib/useDebounce"
import { useMutation, useQuery, useQueryClient } from "react-query"
import axiosClient from "../../../../../../../../lib/axios"
import SearchIcon from '@mui/icons-material/Search';
import LoadingApiQuery from "../../../../../../../../components/LoadingApiQuery"
import { FiscalMovimentosSaida } from "../../../../../../../../types/api/fiscal/pesquisar-movimentos-saida"
import IRequest from "../../../../../../../../types/IRequest"
import { Publico } from "../../../../../../../../types/api/financeiro/datalhar-titulos-receber"
import tratativaDeDatas from "../../../../../../../../helpers/tratativaDeDatas"
import { formatarValorMonetario } from "../../../../../../../../helpers/masks"
import { enqueueSnackbar } from "notistack"
import DisplayNotFoundInfo from "../../../../../../../../components/DisplayNotFindInfo"

export default function DialogSelectMovFiscaisSaída({
    open,
    handleClose,
    publico,
    titulo
}: DialogSelectMovFiscaisSaídaProps) {

    const queryClient = useQueryClient();

    const [keyword, setKeyword] = useState<string>("")
    const [selected, setSelected] = useState<Value>(null)

    //queries e mutations
    const debouncedKeyword = useDebounce(keyword)
    const { data, isLoading } = useQuery({
        queryKey: ['/fiscal/pesquisar-movimentos-saida', debouncedKeyword],
        queryFn: () => axiosClient.get("/fiscal/pesquisar-movimentos-saida", {
            params: {
                tipo_publico_igual: publico.tipo_publico,
                codigo_publico_igual: publico.codigo_publico,
                pesquisar: debouncedKeyword,
            }
        }),
        retry: false,
        cacheTime: 0
    });
    const { dadosMovSaida }: DataQuery = {
        req: data?.data,
        dadosMovSaida: data?.data.data || []
    }

    const { mutate: vincularMov, isLoading: loadingMutation } = useMutation({
        mutationKey: '/financeiro/contas-receber/vincular-movimento-fiscal-saida',
        mutationFn: (inputs: TInputObj) => axiosClient.post("/financeiro/contas-receber/vincular-movimento-fiscal-saida", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar("Erro ao vincular movimento fiscal", { variant: "error" })
        },
        onSuccess: () => {
            queryClient.invalidateQueries('financeiro/contas-receber/detalhar-titulo-receber')
            enqueueSnackbar("Erro ao vincular movimento fiscal", { variant: "success" })
            handleClose()
        }
    })
    //  

    //funções manipuladoras
    const handleSubmit = () => {
        if (!selected) return

        const params: TInputObj = {
            titulo,
            loja_id: selected.loja_id,
            movimento: selected.codigo_formatado
        }
        vincularMov(params)
    }
    //  

    //valores importantes
    const bloquearConfirmar = !selected
    //

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                scroll="body"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle >
                    Selecionar movimentação fiscal de saída
                </DialogTitle>

                <DialogContent>
                    <Box py={2}>
                        <TextField
                            fullWidth
                            value={keyword}
                            label="Pesquisar"
                            onChange={({ target }) => setKeyword(target.value)}
                            slotProps={{
                                input: {
                                    endAdornment:
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                }
                            }}
                        />

                        {isLoading && <LoadingApiQuery extraParams={{ height: 400, my: 1, alignItems: "center" }} />}

                        {
                            !isLoading &&

                            <TableContainer sx={{ height: 400, overflow: "auto", my: 1 }}>
                                {
                                    dadosMovSaida.length === 0 &&
                                    <DisplayNotFoundInfo label="Nenhum movimento encontrado" />
                                }

                                {
                                    dadosMovSaida.length > 0 &&
                                    <Table size="small" aria-label="a dense table" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox"></TableCell>
                                                <TableCell><b>Código</b></TableCell>
                                                <TableCell><b>Data emissão</b></TableCell>
                                                <TableCell align="right"><b>Valor</b></TableCell>
                                                <TableCell><b>UF</b></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dadosMovSaida.map((row) => {
                                                const identificador = row.codigo_formatado
                                                const radioMarcado = selected?.codigo_formatado === row.codigo_formatado

                                                return (
                                                    <TableRow
                                                        key={row.codigo_formatado}
                                                        sx={{
                                                            cursor: "pointer",
                                                            backgroundColor: radioMarcado ? "#edf7ed" : "unset",
                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                        }}
                                                        onClick={() => setSelected(row)}
                                                    >
                                                        <TableCell>
                                                            <Radio
                                                                name="radio-buttons"
                                                                checked={radioMarcado}
                                                                slotProps={{ input: { 'aria-label': identificador } }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{row.codigo_formatado}</TableCell>
                                                        <TableCell>{tratativaDeDatas(row.data_emissao)}</TableCell>
                                                        <TableCell align="right">{formatarValorMonetario(row.valor_contabil)}</TableCell>
                                                        <TableCell>{row.uf_documento}</TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                }
                            </TableContainer>
                        }
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleClose}
                    >
                        Cancelar
                    </Button>

                    <Button
                        autoFocus
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={bloquearConfirmar || loadingMutation}
                    >
                        {loadingMutation && <CircularProgress size={18} sx={{ mr: 1 }} />}
                        Selecionar
                    </Button>
                </DialogActions>
            </Dialog >
        </>
    )
}

interface DialogSelectMovFiscaisSaídaProps {
    open: boolean,
    handleClose: () => void,
    publico: Publico,
    titulo: string
}

type DataQuery = {
    req: IRequest<FiscalMovimentosSaida> | null
    dadosMovSaida: FiscalMovimentosSaida[]
}
type Value = FiscalMovimentosSaida | null

type TInputObj = {
    titulo: string,
    loja_id: number,
    movimento: string
}